import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { taskAttachmentsAPI } from '../utils/supabase'
import { handleRealtimeAttachmentEvent } from './reducers/realtimeReducers'

// Async thunks
export const fetchTaskAttachments = createAsyncThunk(
  'taskAttachments/fetch',
  async (taskId, { getState, rejectWithValue }) => {
    try {
      const state = getState()
      const { loadedTaskIds } = state.taskAttachments

      // Check if we've already loaded this task's attachments
      if (loadedTaskIds.includes(taskId)) {
        return { taskId, attachments: [], alreadyLoaded: true }
      }

      // Fetch attachments for this task (will be merged into global state)
      const { data, error } = await taskAttachmentsAPI.getTaskAttachments(taskId)
      if (error) throw error
      return { taskId, attachments: data || [], alreadyLoaded: false }
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const uploadTaskAttachment = createAsyncThunk(
  'taskAttachments/upload',
  async ({ userId, taskId, file }, { rejectWithValue }) => {
    try {
      const { data, error } = await taskAttachmentsAPI.uploadAttachment(userId, taskId, file)
      if (error) throw error
      return data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const deleteTaskAttachment = createAsyncThunk(
  'taskAttachments/delete',
  async (attachmentId, { rejectWithValue }) => {
    try {
      const { error } = await taskAttachmentsAPI.deleteAttachment(attachmentId)
      if (error) throw error
      return attachmentId
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

// Memory management: Limit cached attachments to prevent unbounded growth
// For power users with many tasks, this prevents memory issues in long sessions
// Attachments are lightweight metadata (~1-2KB each), so 100 tasks supports ~1000 attachments
const MAX_CACHED_TASKS = 100 // Cache attachments for up to 100 tasks

const initialState = {
  attachments: [], // Flat array of ALL user's attachments (Redux-First pattern)
  loading: false,
  error: null,
  initialized: false,
  loadedTaskIds: [] // Track which tasks have been loaded to prevent duplicate fetches
}

const taskAttachmentsSlice = createSlice({
  name: 'taskAttachments',
  initialState,
  reducers: {
    clearTaskAttachments: (state) => {
      // Clear all attachments (called on logout)
      state.attachments = []
      state.initialized = false
      state.error = null
      state.loadedTaskIds = []
    },
    clearError: (state) => {
      state.error = null
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch Task Attachments
      .addCase(fetchTaskAttachments.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchTaskAttachments.fulfilled, (state, action) => {
        state.loading = false
        const { taskId, attachments, alreadyLoaded } = action.payload

        // Memory management: Clean up old cached tasks if limit exceeded
        if (state.loadedTaskIds.length >= MAX_CACHED_TASKS && !state.loadedTaskIds.includes(taskId)) {
          // Remove oldest task from cache (FIFO)
          const oldestTaskId = state.loadedTaskIds.shift()

          // Remove all attachments for the oldest task
          state.attachments = state.attachments.filter(att => att.task_id !== oldestTaskId)

          if (import.meta.env.DEV) {
            console.log(`[TaskAttachments] Cache limit reached (${MAX_CACHED_TASKS}), evicted task:`, oldestTaskId)
          }
        }

        // Mark this task as loaded (add to end of queue for LRU-like behavior)
        if (!state.loadedTaskIds.includes(taskId)) {
          state.loadedTaskIds.push(taskId)
        }

        // Only merge attachments if we actually fetched new ones
        if (!alreadyLoaded && attachments.length > 0) {
          // Merge new attachments with existing (avoid duplicates)
          const newAttachments = attachments.filter(
            newAtt => !state.attachments.find(existing => existing.id === newAtt.id)
          )
          state.attachments.push(...newAttachments)
        }

        state.initialized = true
      })
      .addCase(fetchTaskAttachments.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.initialized = true
      })

      // Upload Task Attachment
      .addCase(uploadTaskAttachment.pending, (state) => {
        state.error = null
      })
      .addCase(uploadTaskAttachment.fulfilled, (state, action) => {
        // Prevent duplicate if realtime INSERT event already added it
        // Race condition: Upload completes → Redux adds → Realtime fires → potential duplicate
        if (!state.attachments.find(att => att.id === action.payload.id)) {
          state.attachments.push(action.payload)
        }
      })
      .addCase(uploadTaskAttachment.rejected, (state, action) => {
        state.error = action.payload
      })

      // Delete Task Attachment
      .addCase(deleteTaskAttachment.pending, (state) => {
        state.error = null
      })
      .addCase(deleteTaskAttachment.fulfilled, (state, action) => {
        state.attachments = state.attachments.filter(att => att.id !== action.payload)
      })
      .addCase(deleteTaskAttachment.rejected, (state, action) => {
        state.error = action.payload
      })

      // Realtime attachment events - Global state pattern
      .addCase(handleRealtimeAttachmentEvent.fulfilled, (state, action) => {
        if (!action.payload) return

        const { type, record } = action.payload

        switch (type) {
          case 'INSERT':
            // Add to global state (avoid duplicates)
            // NOTE: We intentionally add attachments even for unloaded tasks because:
            // 1. Multi-device sync requires immediate visibility across all devices
            // 2. Attachments are lightweight metadata (~1-2KB each), negligible memory impact
            // 3. When user opens the task later, attachments are already present
            // 4. loadedTaskIds still prevents duplicate API fetches
            if (!state.attachments.find(att => att.id === record.id)) {
              if (import.meta.env.DEV) {
                console.log('[Realtime] Adding new attachment to global state:', record)
              }
              state.attachments.push(record)
            }
            break

          case 'UPDATE': {
            // Update in global state if exists
            const attachmentIndex = state.attachments.findIndex(att => att.id === record.id)
            if (attachmentIndex !== -1) {
              if (import.meta.env.DEV) {
                console.log('[Realtime] Updating attachment in global state:', record)
              }
              state.attachments[attachmentIndex] = { ...state.attachments[attachmentIndex], ...record }
            } else {
              // If not in state yet, add it (handles edge case where attachment was created before state initialized)
              if (import.meta.env.DEV) {
                console.log('[Realtime] Adding updated attachment to global state:', record)
              }
              state.attachments.push(record)
            }
            break
          }

          case 'DELETE':
            // Remove from global state if present
            if (record?.id) {
              if (import.meta.env.DEV) {
                console.log('[Realtime] Removing attachment from global state:', record.id)
              }
              state.attachments = state.attachments.filter(att => att.id !== record.id)
            }
            break
        }
      })
  }
})

export const { clearTaskAttachments, clearError } = taskAttachmentsSlice.actions
export default taskAttachmentsSlice.reducer
